import { Link, Outlet, useNavigate } from '@tanstack/react-router';
import { CheckCircleIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import {
  useAuthStore,
  useLogout,
  useRefreshToken,
} from '../../services/cloudflare';
import SearchBar from '../SearchBar';
import { Input } from '../ui/input';
import { cn } from '../ui/lib/utils';
import { Footer } from './Footer';
import { HeaderLeft } from './HeaderLeft';
import { LayoutRightSideNav } from './LayoutRightSideNav';

export function RootLayout() {
  const { isAuthenticated, checkTokenExpiry, isRefreshing } = useAuthStore();
  const refreshTokenMutation = useRefreshToken();
  const navigate = useNavigate();
  const [sessionExpired, setSessionExpired] = useState(false);

  // Handle token expiration check
  useEffect(() => {
    if (isAuthenticated) {
      // Initial check
      if (!checkTokenExpiry() && !isRefreshing) {
        // Try to refresh the token silently
        handleTokenRefresh();
      }

      // Set up periodic checks (every minute)
      const checkInterval = setInterval(() => {
        if (!checkTokenExpiry() && !isRefreshing) {
          handleTokenRefresh();
        }
      }, 60 * 1000);

      return () => clearInterval(checkInterval);
    }
  }, [isAuthenticated, checkTokenExpiry, isRefreshing]);

  // Handle authentication state changes
  useEffect(() => {
    // If not authenticated and not already on the home page, redirect to home
    if (!isAuthenticated && window.location.pathname !== '/') {
      navigate({ to: '/' });
    }
  }, [isAuthenticated, navigate]);

  // Handle token refresh
  const handleTokenRefresh = async () => {
    try {
      const success = await refreshTokenMutation.mutateAsync();
      if (!success) {
        handleSessionExpired();
      }
    } catch (error) {
      console.error('Token refresh failed:', error);
      handleSessionExpired();
    }
  };

  // Handle session expiration
  const handleSessionExpired = () => {
    setSessionExpired(true);
    navigate({ to: '/' });

    // Reset expired flag after 5 seconds
    setTimeout(() => {
      setSessionExpired(false);
    }, 5000);
  };

  return (
    <div className="min-h-screen dark bg-background flex flex-col">
      {sessionExpired && (
        <div className="bg-red-500 text-white py-2 text-center font-medium">
          Your session has expired. Please log in again.
        </div>
      )}
      <header className="border-b bg-card/80 px-4 backdrop-blur-sm sticky top-0 z-10">
        <div className="container py-4 flex mx-auto justify-between content-center items-center">
          <HeaderLeft isAuthenticated={isAuthenticated} />
          <SearchBar />

          {isAuthenticated && <LayoutRightSideNav />}
        </div>
      </header>
      <main className=" content-center relative flex-1">
        <Outlet />
      </main>
      <Footer />
    </div>
  );
}
